import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import { customerService } from '../../services/api'
import { 
  Customer, 
  CreateCustomerRequest, 
  UpdateCustomerRequest, 
  CustomerFilters,
  PaginationParams,
  SearchSuggestion
} from '../../types/api'
import { isApiError } from '../../utils/api'

interface CustomerState {
  customers: Customer[]
  currentCustomer: Customer | null
  pagination: {
    page: number
    per_page: number
    total: number
    last_page: number
  }
  filters: CustomerFilters
  loading: boolean
  creating: boolean
  updating: boolean
  deleting: boolean
  error: string | null
  searchResults: Customer[]
  searchSuggestions: SearchSuggestion[]
  searchLoading: boolean
  recentCustomers: Customer[]
  topCustomers: Array<{
    customer: Customer
    total_amount: number
    invoice_count: number
  }>
  customerStatistics: {
    total_invoices: number
    total_amount: number
    paid_amount: number
    outstanding_amount: number
    average_order_value: number
    last_purchase_date?: string
  } | null
  creditStatus: {
    credit_limit: number
    used_credit: number
    available_credit: number
    credit_utilization: number
  } | null
  bulkOperationStatus: {
    operation: 'update' | 'delete' | 'export' | 'import' | null
    progress: number
    completed: number
    failed: number
    total: number
  }
}

const initialState: CustomerState = {
  customers: [],
  currentCustomer: null,
  pagination: {
    page: 1,
    per_page: 10,
    total: 0,
    last_page: 1
  },
  filters: {},
  loading: false,
  creating: false,
  updating: false,
  deleting: false,
  error: null,
  searchResults: [],
  searchSuggestions: [],
  searchLoading: false,
  recentCustomers: [],
  topCustomers: [],
  customerStatistics: null,
  creditStatus: null,
  bulkOperationStatus: {
    operation: null,
    progress: 0,
    completed: 0,
    failed: 0,
    total: 0
  }
}

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (params: { filters?: CustomerFilters; pagination?: PaginationParams }, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomers(params.filters, params.pagination)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch customers')
    }
  }
)

export const fetchCustomer = createAsyncThunk(
  'customers/fetchCustomer',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomer(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch customer')
    }
  }
)

export const createCustomer = createAsyncThunk(
  'customers/createCustomer',
  async (data: CreateCustomerRequest, { rejectWithValue }) => {
    try {
      const response = await customerService.createCustomer(data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to create customer')
    }
  }
)

export const updateCustomer = createAsyncThunk(
  'customers/updateCustomer',
  async ({ id, data }: { id: string; data: UpdateCustomerRequest }, { rejectWithValue }) => {
    try {
      const response = await customerService.updateCustomer(id, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update customer')
    }
  }
)

export const deleteCustomer = createAsyncThunk(
  'customers/deleteCustomer',
  async (id: string, { rejectWithValue }) => {
    try {
      await customerService.deleteCustomer(id)
      return id
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to delete customer')
    }
  }
)

export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await customerService.searchCustomers(query)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to search customers')
    }
  }
)

export const getCustomerSuggestions = createAsyncThunk(
  'customers/getSuggestions',
  async (query: string, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerSuggestions(query)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to get customer suggestions')
    }
  }
)

export const fetchCustomerByPhone = createAsyncThunk(
  'customers/fetchByPhone',
  async (phone: string, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerByPhone(phone)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch customer by phone')
    }
  }
)

export const fetchCustomerStatistics = createAsyncThunk(
  'customers/fetchStatistics',
  async ({ id, period }: { id: string; period?: string }, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomerStatistics(id, period)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch customer statistics')
    }
  }
)

export const fetchTopCustomers = createAsyncThunk(
  'customers/fetchTopCustomers',
  async ({ limit = 10, period }: { limit?: number; period?: string }, { rejectWithValue }) => {
    try {
      const response = await customerService.getTopCustomers(limit, period)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch top customers')
    }
  }
)

export const fetchRecentCustomers = createAsyncThunk(
  'customers/fetchRecent',
  async (limit: number = 10, { rejectWithValue }) => {
    try {
      const response = await customerService.getRecentCustomers(limit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch recent customers')
    }
  }
)

export const activateCustomer = createAsyncThunk(
  'customers/activate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await customerService.activateCustomer(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to activate customer')
    }
  }
)

export const deactivateCustomer = createAsyncThunk(
  'customers/deactivate',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await customerService.deactivateCustomer(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to deactivate customer')
    }
  }
)

export const updateCreditLimit = createAsyncThunk(
  'customers/updateCreditLimit',
  async ({ id, creditLimit }: { id: string; creditLimit: number }, { rejectWithValue }) => {
    try {
      const response = await customerService.updateCreditLimit(id, creditLimit)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to update credit limit')
    }
  }
)

export const fetchCreditStatus = createAsyncThunk(
  'customers/fetchCreditStatus',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await customerService.getCreditStatus(id)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to fetch credit status')
    }
  }
)

export const bulkUpdateCustomers = createAsyncThunk(
  'customers/bulkUpdate',
  async ({ ids, data }: { ids: string[]; data: Partial<UpdateCustomerRequest> }, { rejectWithValue }) => {
    try {
      const response = await customerService.bulkUpdate(ids, data)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk update customers')
    }
  }
)

export const bulkDeleteCustomers = createAsyncThunk(
  'customers/bulkDelete',
  async (ids: string[], { rejectWithValue }) => {
    try {
      const response = await customerService.bulkDelete(ids)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to bulk delete customers')
    }
  }
)

export const importCustomers = createAsyncThunk(
  'customers/import',
  async (file: File, { rejectWithValue }) => {
    try {
      const response = await customerService.importCustomers(file)
      return response.data
    } catch (error) {
      if (isApiError(error)) {
        return rejectWithValue(error.message)
      }
      return rejectWithValue('Failed to import customers')
    }
  }
)

// Create slice
const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setFilters: (state, action: PayloadAction<CustomerFilters>) => {
      state.filters = action.payload
    },
    updateFilters: (state, action: PayloadAction<Partial<CustomerFilters>>) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    clearFilters: (state) => {
      state.filters = {}
    },
    setPagination: (state, action: PayloadAction<Partial<typeof initialState.pagination>>) => {
      state.pagination = { ...state.pagination, ...action.payload }
    },
    setCurrentCustomer: (state, action: PayloadAction<Customer | null>) => {
      state.currentCustomer = action.payload
    },
    clearSearchResults: (state) => {
      state.searchResults = []
      state.searchSuggestions = []
    },
    clearCustomerStatistics: (state) => {
      state.customerStatistics = null
    },
    clearCreditStatus: (state) => {
      state.creditStatus = null
    },
    resetBulkOperationStatus: (state) => {
      state.bulkOperationStatus = {
        operation: null,
        progress: 0,
        completed: 0,
        failed: 0,
        total: 0
      }
    }
  },
  extraReducers: (builder) => {
    // Fetch Customers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false
        state.customers = action.payload.data
        state.pagination = {
          page: action.payload.page,
          per_page: action.payload.per_page,
          total: action.payload.total,
          last_page: action.payload.last_page
        }
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch Single Customer
    builder
      .addCase(fetchCustomer.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCustomer.fulfilled, (state, action) => {
        state.loading = false
        state.currentCustomer = action.payload
      })
      .addCase(fetchCustomer.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Create Customer
    builder
      .addCase(createCustomer.pending, (state) => {
        state.creating = true
        state.error = null
      })
      .addCase(createCustomer.fulfilled, (state, action) => {
        state.creating = false
        state.customers.unshift(action.payload)
        state.currentCustomer = action.payload
      })
      .addCase(createCustomer.rejected, (state, action) => {
        state.creating = false
        state.error = action.payload as string
      })

    // Update Customer
    builder
      .addCase(updateCustomer.pending, (state) => {
        state.updating = true
        state.error = null
      })
      .addCase(updateCustomer.fulfilled, (state, action) => {
        state.updating = false
        const index = state.customers.findIndex(customer => customer.id === action.payload.id)
        if (index !== -1) {
          state.customers[index] = action.payload
        }
        if (state.currentCustomer?.id === action.payload.id) {
          state.currentCustomer = action.payload
        }
      })
      .addCase(updateCustomer.rejected, (state, action) => {
        state.updating = false
        state.error = action.payload as string
      })

    // Delete Customer
    builder
      .addCase(deleteCustomer.pending, (state) => {
        state.deleting = true
        state.error = null
      })
      .addCase(deleteCustomer.fulfilled, (state, action) => {
        state.deleting = false
        state.customers = state.customers.filter(customer => customer.id !== action.payload)
        if (state.currentCustomer?.id === action.payload) {
          state.currentCustomer = null
        }
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.deleting = false
        state.error = action.payload as string
      })

    // Search Customers
    builder
      .addCase(searchCustomers.pending, (state) => {
        state.searchLoading = true
      })
      .addCase(searchCustomers.fulfilled, (state, action) => {
        state.searchLoading = false
        state.searchResults = action.payload
      })
      .addCase(searchCustomers.rejected, (state, action) => {
        state.searchLoading = false
        state.error = action.payload as string
      })

    // Get Customer Suggestions
    builder
      .addCase(getCustomerSuggestions.fulfilled, (state, action) => {
        state.searchSuggestions = action.payload
      })
      .addCase(getCustomerSuggestions.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Customer by Phone
    builder
      .addCase(fetchCustomerByPhone.fulfilled, (state, action) => {
        if (action.payload) {
          state.currentCustomer = action.payload
        }
      })
      .addCase(fetchCustomerByPhone.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Customer Statistics
    builder
      .addCase(fetchCustomerStatistics.fulfilled, (state, action) => {
        state.customerStatistics = action.payload
      })
      .addCase(fetchCustomerStatistics.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Top Customers
    builder
      .addCase(fetchTopCustomers.fulfilled, (state, action) => {
        state.topCustomers = action.payload
      })
      .addCase(fetchTopCustomers.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Recent Customers
    builder
      .addCase(fetchRecentCustomers.fulfilled, (state, action) => {
        state.recentCustomers = action.payload
      })
      .addCase(fetchRecentCustomers.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Activate/Deactivate Customer
    builder
      .addCase(activateCustomer.fulfilled, (state, action) => {
        const index = state.customers.findIndex(customer => customer.id === action.payload.id)
        if (index !== -1) {
          state.customers[index] = action.payload
        }
        if (state.currentCustomer?.id === action.payload.id) {
          state.currentCustomer = action.payload
        }
      })
      .addCase(deactivateCustomer.fulfilled, (state, action) => {
        const index = state.customers.findIndex(customer => customer.id === action.payload.id)
        if (index !== -1) {
          state.customers[index] = action.payload
        }
        if (state.currentCustomer?.id === action.payload.id) {
          state.currentCustomer = action.payload
        }
      })

    // Update Credit Limit
    builder
      .addCase(updateCreditLimit.fulfilled, (state, action) => {
        const index = state.customers.findIndex(customer => customer.id === action.payload.id)
        if (index !== -1) {
          state.customers[index] = action.payload
        }
        if (state.currentCustomer?.id === action.payload.id) {
          state.currentCustomer = action.payload
        }
      })
      .addCase(updateCreditLimit.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Fetch Credit Status
    builder
      .addCase(fetchCreditStatus.fulfilled, (state, action) => {
        state.creditStatus = action.payload
      })
      .addCase(fetchCreditStatus.rejected, (state, action) => {
        state.error = action.payload as string
      })

    // Bulk Operations
    builder
      .addCase(bulkUpdateCustomers.pending, (state) => {
        state.bulkOperationStatus.operation = 'update'
      })
      .addCase(bulkUpdateCustomers.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.updated,
          failed: action.payload.failed.length,
          total: action.payload.updated + action.payload.failed.length
        }
      })
      .addCase(bulkUpdateCustomers.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })

    builder
      .addCase(bulkDeleteCustomers.pending, (state) => {
        state.bulkOperationStatus.operation = 'delete'
      })
      .addCase(bulkDeleteCustomers.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.deleted,
          failed: action.payload.failed.length,
          total: action.payload.deleted + action.payload.failed.length
        }
      })
      .addCase(bulkDeleteCustomers.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })

    // Import Customers
    builder
      .addCase(importCustomers.pending, (state) => {
        state.bulkOperationStatus.operation = 'import'
      })
      .addCase(importCustomers.fulfilled, (state, action) => {
        state.bulkOperationStatus = {
          operation: null,
          progress: 100,
          completed: action.payload.imported,
          failed: action.payload.failed,
          total: action.payload.imported + action.payload.failed
        }
      })
      .addCase(importCustomers.rejected, (state, action) => {
        state.bulkOperationStatus.operation = null
        state.error = action.payload as string
      })
  },
})

// Export actions
export const {
  clearError,
  setFilters,
  updateFilters,
  clearFilters,
  setPagination,
  setCurrentCustomer,
  clearSearchResults,
  clearCustomerStatistics,
  clearCreditStatus,
  resetBulkOperationStatus
} = customerSlice.actions

// Selectors
export const selectCustomers = (state: { customers: CustomerState }) => state.customers.customers
export const selectCurrentCustomer = (state: { customers: CustomerState }) => state.customers.currentCustomer
export const selectCustomerPagination = (state: { customers: CustomerState }) => state.customers.pagination
export const selectCustomerFilters = (state: { customers: CustomerState }) => state.customers.filters
export const selectCustomerLoading = (state: { customers: CustomerState }) => state.customers.loading
export const selectCustomerError = (state: { customers: CustomerState }) => state.customers.error
export const selectCustomerSearchResults = (state: { customers: CustomerState }) => state.customers.searchResults
export const selectCustomerSuggestions = (state: { customers: CustomerState }) => state.customers.searchSuggestions
export const selectRecentCustomers = (state: { customers: CustomerState }) => state.customers.recentCustomers
export const selectTopCustomers = (state: { customers: CustomerState }) => state.customers.topCustomers
export const selectCustomerStatistics = (state: { customers: CustomerState }) => state.customers.customerStatistics
export const selectCreditStatus = (state: { customers: CustomerState }) => state.customers.creditStatus
export const selectCustomerBulkOperationStatus = (state: { customers: CustomerState }) => state.customers.bulkOperationStatus

export default customerSlice.reducer 